import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, LoaderCircle, Search, X } from "lucide-react";
import { LuEye } from "react-icons/lu";
import { LuEyeOff } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";

import { Badge } from "../ui/badge";
import { Button, StatefulButton } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { useAppToast } from "../motion/toast-provider";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
  deleteIntegration,
  requestIntegrationsOverview,
  saveIntegration,
} from "./integrationActions";
import {
  selectIntegrationCards,
  selectIntegrationGroups,
  selectIntegrationSummary,
  selectIntegrationsError,
  selectIntegrationsMutation,
  selectIntegrationsStatus,
} from "./integrationSelectors";

import "./integrations.css";

const STATUS_TABS = [
  { key: "all", label: "All integrations" },
  { key: "connected", label: "Connected" },
];

const getGroupId = (item) => item?.id ?? item?.group_id ?? item?.groupId;
const getConnectionGroupId = (connection) => connection?.group_id ?? connection?.groupId;

const getToastFailureMessage = (message, fallback) => {
  const normalizedMessage = typeof message === "string" ? message.trim() : "";
  const statusMarker = " with status code ";
  const statusIndex = normalizedMessage.toLowerCase().lastIndexOf(statusMarker);
  const withoutStatusCode = statusIndex === -1 ? normalizedMessage : normalizedMessage.slice(0, statusIndex).trim();
  return withoutStatusCode || fallback;
};

const getConnectionForGroup = (provider, group) => {
  const groupId = getGroupId(group) ?? group;
  if (groupId === undefined || groupId === null || groupId === "") return null;

  const normalizedGroupId = String(groupId);
  return (Array.isArray(provider?.connections) ? provider.connections : []).find((connection) => {
    const connectionGroupId = getConnectionGroupId(connection);
    return connectionGroupId !== undefined
      && connectionGroupId !== null
      && connectionGroupId !== ""
      && String(connectionGroupId) === normalizedGroupId;
  }) || null;
};

const getInitialValues = (provider, connection) =>
  provider.fields.reduce((values, field) => {
    const value = connection?.credentials?.[field.payloadKey];
    values[field.payloadKey] = value ?? (field.type === "checkbox" ? false : "");
    return values;
  }, {});

function IntegrationCard({ provider, onConnect }) {
  const connected = provider.connections.length > 0;

  return (
    <article className="integrations-card">
      <div className="integration-card-header">
        <div className="integrations-card-icon-wrap">
          <img loading="lazy" alt={`${provider.label} logo`} className="integrations-card-icon" src={provider.icon} />
        </div>
        {connected && (
        <Badge className="integrations-status" variant="success">
          {provider.connectedGroups} group{provider.connectedGroups === 1 ? "" : "s"} connected
        </Badge>
        )}
      </div>

      <div className="integrations-card-copy">
        <h2>{provider.label}</h2>
        <p>{provider.description}</p>
      </div>

      <div className="integrations-card-actions">
        <div className="integrations-connect-group">
          <Button
            className="integrations-connect"
            onClick={() => onConnect(provider)}
            size="sm"
            type="button"
            variant="primary"
          >
            <ArrowRight aria-hidden="true" size={16} />
            {connected ? "Manage" : "Connect"}
          </Button>
        </div>
        <a
          className="integrations-help"
          href={provider.setupGuide}
          rel="noreferrer"
          target="_blank"
        >
          Help
        </a>
      </div>
    </article>
  );
}

function IntegrationGroupPicker({ provider, groups, onChoose, onClose }) {
  const groupSearchRef = useRef(null);
  const [groupSearch, setGroupSearch] = useState("");

  const filteredGroups = useMemo(() => {
    const query = groupSearch.trim().toLowerCase();
    return groups.filter((group) => !query || String(group.name || "").toLowerCase().includes(query));
  }, [groupSearch, groups]);
  const groupColumnCount = Math.ceil(filteredGroups.length / 5);
  const visibleColumnCount = Math.min(groupColumnCount, 4);

  useEffect(() => {
    groupSearchRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      aria-labelledby="integration-group-picker-title"
      aria-modal="true"
      className="integrations-dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
      role="dialog"
    >
      <div className="integrations-dialog integrations-group-picker">
        <div className="integrations-dialog-header">
          <div>
            <h2 id="integration-group-picker-title">Configure {provider.label}</h2>
          </div>
          <Button
            aria-label="Close dialog"
            className="integrations-dialog-close"
            onClick={onClose}
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            <X aria-hidden="true" size={20} />
          </Button>
        </div>

        <div className="integrations-group-picker-toolbar">
          <p>Select a group to connect/configure</p>
          <label className="integrations-group-search">
            <span className="sr-only">Search groups</span>
            <Input
              aria-label="Search groups"
              classNames={{
                input: "integrations-group-search-control",
                root: "integrations-group-search-field",
                shell: "integrations-group-search-shell",
              }}
              leftIcon={<Search aria-hidden="true" size={16} />}
              onValueChange={setGroupSearch}
              placeholder="Search groups"
              ref={groupSearchRef}
              type="search"
              value={groupSearch}
              variant="ui"
            />
          </label>
        </div>

        {filteredGroups.length ? (
          <div
            aria-label="Available groups"
            className={`integrations-group-list ${groupColumnCount > 4 ? "integrations-group-list--overflow" : ""}`.trim()}
            role="group"
            style={{
              "--group-column-count": visibleColumnCount,
              "--group-row-count": Math.min(filteredGroups.length, 5),
            }}
            tabIndex={0}
          >
            {filteredGroups.map((group) => {
              const connected = Boolean(getConnectionForGroup(provider, group));
              return (
                <button
                  className="integrations-group-option"
                  key={group.id}
                  onClick={() => onChoose(group)}
                  type="button"
                >
                  <div className="integrations-group-option-copy">
                    <strong title={group.name}>{group.name}</strong>
                    {connected ? (
                      <Badge variant="success">Connected</Badge>
                    ) : (
                      <small>Not connected yet</small>
                    )}
                  </div>
                  <ChevronRight aria-hidden="true" size={18} />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="integrations-group-empty">
            <strong>{groups.length ? "No groups match your search" : "No groups available"}</strong>
            <p>{groups.length ? "Try a different group name." : "Add a group before connecting an integration."}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function IntegrationDialog({
  provider,
  group,
  connection,
  values,
  isSaving,
  isDeleting,
  submitState,
  hasFormChanges,
  onChange,
  onBack,
  onClose,
  onDelete,
  onSubmit,
}) {
  const firstInputRef = useRef(null);
  const [visibleSecrets, setVisibleSecrets] = useState({});

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      aria-labelledby="integration-dialog-title"
      aria-modal="true"
      className="integrations-dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
        role="dialog"
    >
      <form autoComplete="off" className="integrations-dialog" onSubmit={onSubmit}>
        <div className="integrations-dialog-header">
          <div className="integrations-dialog-title-row">
            <Button
              aria-label="Back to integration groups"
              className="integrations-dialog-back"
              onClick={onBack}
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <ChevronLeft aria-hidden="true" size={20} />
            </Button>
            <h2 id="integration-dialog-title">Configure {provider.label}</h2>
          </div>
          <Button
            aria-label="Close dialog"
            className="integrations-dialog-close"
            onClick={onClose}
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            <X aria-hidden="true" size={20} />
          </Button>
        </div>

        <div className="integrations-form-grid">
          {provider.fields.map((field, index) => {
            const isSecretField = field.type === "password";
            const isSecretVisible = Boolean(visibleSecrets[field.payloadKey]);

            return (
              <label
                className={`${field.type === "checkbox" ? "integrations-checkbox" : "integrations-field"}${provider.key === "google-sheet" && field.payloadKey === "gurl" ? " integrations-field--full-width" : ""}`}
                key={field.payloadKey}
              >
                {field.type === "checkbox" ? (
                  <>
                    <input
                      checked={Boolean(values[field.payloadKey])}
                      onChange={(event) => onChange(field.payloadKey, event.target.checked)}
                      type="checkbox"
                    />
                    <span>{field.label}{field.required && <span className="integrations-required-mark"> *</span>}</span>
                  </>
                ) : (
                  <>
                    <span>{field.label}{field.required && <span className="integrations-required-mark"> *</span>}</span>
                    <Input
                      autoComplete={isSecretField ? "new-password" : "off"}
                      classNames={{
                        input: "integrations-field-input-control",
                        root: "integrations-field-input-field",
                        shell: "integrations-field-input-shell",
                      }}
                      data-1p-ignore="true"
                      data-lpignore="true"
                      name={`integration-${provider.key}-${field.payloadKey}`}
                      onValueChange={(value) => onChange(field.payloadKey, value)}
                      placeholder={field.placeholder}
                      ref={index === 0 ? firstInputRef : undefined}
                      required={field.required}
                      rightIcon={isSecretField ? (
                        <Button
                          aria-label={isSecretVisible ? `Hide ${field.label}` : `Show ${field.label}`}
                          className="integrations-secret-toggle"
                          onClick={() => setVisibleSecrets((current) => ({
                            ...current,
                            [field.payloadKey]: !current[field.payloadKey],
                          }))}
                          onMouseDown={(event) => event.preventDefault()}
                          size="icon-xs"
                          type="button"
                          variant="ghost"
                        >
                          {isSecretVisible ? <LuEyeOff aria-hidden="true" size={16} /> : <LuEye aria-hidden="true" size={16} />}
                        </Button>
                      ) : null}
                      type={isSecretField && isSecretVisible ? "text" : field.type}
                      value={values[field.payloadKey] || ""}
                      variant="ui"
                    />
                  </>
                )}
              </label>
            );
          })}
        </div>

        <div className="integrations-dialog-footer">
          {connection ? (
            <Button
              className="integrations-delete"
              disabled={isDeleting || isSaving}
              onClick={onDelete}
              size="sm"
              type="button"
              variant="link"
            >
              {isDeleting ? <LoaderCircle className="is-spinning" size={15} /> : null}
              Remove
            </Button>
          ) : <span />}
          <div className="integrations-dialog-actions">
            <StatefulButton
              className="integrations-submit"
              disabled={isDeleting || !hasFormChanges}
              errorText="Try again"
              loadingText="Connecting"
              state={submitState}
              type="submit"
              variant="primary"
            >
              Connect
            </StatefulButton>
          </div>
        </div>
      </form>
    </div>
  );
}

function IntegrationDeleteDialog({ provider, group, isDeleting, onCancel, onConfirm }) {
  const cancelRef = useRef(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isDeleting) onCancel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isDeleting, onCancel]);

  return (
    <div
      aria-labelledby="integration-delete-dialog-title"
      aria-modal="true"
      className="integrations-dialog-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && !isDeleting && onCancel()}
      role="dialog"
    >
      <div className="integrations-dialog integrations-delete-dialog">
        <div className="integrations-dialog-header">
          <div>
            <h2 id="integration-delete-dialog-title">Remove {provider.label}?</h2>
          </div>
          <Button
            aria-label="Close confirmation dialog"
            className="integrations-dialog-close"
            disabled={isDeleting}
            onClick={onCancel}
            size="icon-sm"
            type="button"
            variant="ghost"
          >
            <X aria-hidden="true" size={20} />
          </Button>
        </div>

        <p className="integrations-delete-dialog-copy">
          This will remove the integration and its saved credentials from <strong>{group.name}</strong>.
        </p>

        <div className="integrations-delete-dialog-actions">
          <Button
            ref={cancelRef}
            disabled={isDeleting}
            onClick={onCancel}
            size="sm"
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className="integrations-delete-confirm"
            disabled={isDeleting}
            onClick={onConfirm}
            size="sm"
            type="button"
            variant="destructive"
          >
            {isDeleting ? <LoaderCircle className="is-spinning" size={15} /> : null}
            Remove integration
          </Button>
        </div>
      </div>
    </div>
  );
}


function Integrations() {
  const dispatch = useDispatch();
  const cards = useSelector(selectIntegrationCards);
  const groups = useSelector(selectIntegrationGroups);
  const summary = useSelector(selectIntegrationSummary);
  const status = useSelector(selectIntegrationsStatus);
  const error = useSelector(selectIntegrationsError);
  const mutation = useSelector(selectIntegrationsMutation);
  const { showToast } = useAppToast();
  const previousOverviewStatus = useRef(status);
  const previousMutationStatus = useRef(mutation.status);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [groupPickerProvider, setGroupPickerProvider] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [initialFormValues, setInitialFormValues] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  useEffect(() => {
    dispatch(requestIntegrationsOverview());
  }, [dispatch]);

  useEffect(() => {
    if (status === "failed" && previousOverviewStatus.current !== "failed") {
      showToast({
        status: "error",
        title: "Failed to load integrations",
        description: getToastFailureMessage(error, "Request failed."),
      });
    }
    previousOverviewStatus.current = status;
  }, [error, showToast, status]);

  useEffect(() => {
    if (mutation.status === "succeeded" && previousMutationStatus.current !== "succeeded") {
      showToast({ status: "success", title: mutation.type === "delete" ? "Integration removed" : "Integration connected" });
      setDialog(null);
      setDeleteConfirmation(null);
      setFormValues({});
      setInitialFormValues({});
    }
    if (mutation.status === "failed" && previousMutationStatus.current !== "failed") {
      showToast({
        status: "error",
        title: mutation.type === "delete" ? "Failed to remove integration" : "Failed to connect integration",
        description: getToastFailureMessage(mutation.error, "Request failed."),
      });
    }
    previousMutationStatus.current = mutation.status;
  }, [mutation, showToast]);

  const filteredCards = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return cards.filter((provider) => {
      const connected = provider.connections.length > 0;
      const searchable = `${provider.label} ${provider.description}`.toLowerCase();
      return (!query || searchable.includes(query)) && (statusFilter === "all" || (statusFilter === "connected" ? connected : !connected));
    });
  }, [cards, searchTerm, statusFilter]);

  const openProvider = (provider) => {
    setGroupPickerProvider(provider);
  };

  const chooseGroup = (group) => {
    const provider = groupPickerProvider;
    if (!provider) return;
    const connection = getConnectionForGroup(provider, group);
    const nextFormValues = getInitialValues(provider, connection);
    setDialog({ provider, group, connection });
    setFormValues(nextFormValues);
    setInitialFormValues(nextFormValues);
    setGroupPickerProvider(null);
  };

  const handleBackToGroups = () => {
    if (!dialog || isSaving || isDeleting) return;
    setGroupPickerProvider(dialog.provider);
    setDialog(null);
    setFormValues({});
    setInitialFormValues({});
  };

  const handleCloseDialog = () => {
    if (isSaving || isDeleting) return;
    setDialog(null);
    setFormValues({});
    setInitialFormValues({});
  };

  const handleFieldChange = (key, value) => {
    setFormValues((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const missingField = dialog.provider.fields.find(
      (field) => field.required && field.type !== "checkbox" && !String(formValues[field.payloadKey] || "").trim(),
    );
    if (missingField) {
      showToast({
        status: "error",
        title: "Required field missing",
        description: `${missingField.label} is required.`,
      });
      return;
    }

    const credentials = dialog.provider.fields.reduce((payload, field) => {
      const value = formValues[field.payloadKey];
      if (field.type === "checkbox" || (value !== undefined && String(value).trim() !== "")) {
        payload[field.payloadKey] = field.type === "checkbox" ? Boolean(value) : value;
      }
      return payload;
    }, {});

    dispatch(saveIntegration({
      name: dialog.connection?.providerName || dialog.provider.key,
      group_id: getGroupId(dialog.group),
      group_name: dialog.group.name,
      credentials,
    }));
  };

  const handleDelete = () => {
    if (!dialog.connection?.id || isSaving || isDeleting) return;
    setDeleteConfirmation({
      connectionId: dialog.connection.id,
      group: dialog.group,
      provider: dialog.provider,
    });
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setDeleteConfirmation(null);
  };

  const confirmDelete = () => {
    if (!deleteConfirmation || isDeleting) return;
    dispatch(deleteIntegration({
      connectionId: deleteConfirmation.connectionId,
      providerKey: deleteConfirmation.provider.key,
    }));
  };

  const isLoading = status === "loading" && !cards.length;
  const isSaving = mutation.status === "saving";
  const isDeleting = mutation.status === "deleting";
  const hasFormChanges = Boolean(
    dialog?.provider.fields.some((field) => formValues[field.payloadKey] !== initialFormValues[field.payloadKey]),
  );

  return (
    <main className="integrations-page">
      <section aria-labelledby="integrations-title" className="integrations-page-shell">
        <div className="integrations-header dashboard-page-heading dashboard-page-heading--title-only">
          <h1 className="dashboard-page-title" id="integrations-title">Integrations</h1>
        </div>

      <div aria-label="Integration summary" className="integrations-summary" role="region">
        {isLoading ? (
          Array.from({ length: 4 }, (_, index) => <div className="integrations-summary-skeleton" key={index} />)
        ) : (
          [
            {
              label: "Connected providers",
              value: summary.connected_providers,
              context: `${summary.total_connections} total connection${summary.total_connections === 1 ? "" : "s"}`,
            },
            {
              label: "Total connections",
              value: summary.total_connections,
              context: "Across all groups",
            },
            {
              label: "Groups with integrations",
              value: summary.groups_with_integrations,
              context: `Of ${summary.total_groups} total group${summary.total_groups === 1 ? "" : "s"}`,
            },
            {
              label: "Total groups",
              value: summary.total_groups,
              context: "Available to configure",
            },
          ].map((metric) => (
            <article className="integrations-summary-card" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              <small>{metric.context}</small>
            </article>
          ))
        )}
      </div>

      <div className="integrations-controls">
        <Tabs
          className="integrations-tabs"
          onValueChange={setStatusFilter}
          value={statusFilter}
          variant="segment"
        >
          <TabsList aria-label="Integration status" className="integrations-tabs-list">
            {STATUS_TABS.map((tab) => (
              <TabsTrigger
                className="integrations-tab-trigger"
                indicatorClassName="integrations-tab-indicator"
                key={tab.key}
                value={tab.key}
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <label className="integrations-search">
          <span className="sr-only">Search integrations</span>
          <Input
            aria-label="Search integrations"
            className="integrations-search-input"
            classNames={{ input: "integrations-search-input", root: "integrations-search-input-shell" }}
            leftIcon={<Search size={17} />}
            onValueChange={setSearchTerm}
            placeholder="Search integrations"
            type="search"
            value={searchTerm}
            variant="ui"
          />
        </label>
      </div>

      <div className="integrations-grid">
        {isLoading
          ? Array.from({ length: 10 }, (_, index) => <Skeleton className="integrations-skeleton" key={index} />)
          : filteredCards.map((provider) => (
            <IntegrationCard key={provider.key} onConnect={openProvider} provider={provider} />
          ))}
      </div>

      {!isLoading && filteredCards.length === 0 && (
        <div className="integrations-empty-state">
          {error ? (
            <><h2>Unable to load integrations</h2><p>We couldn't load the integration catalog. Try again.</p><Button onClick={() => dispatch(requestIntegrationsOverview())} size="sm" type="button" variant="secondary">Retry</Button></>
          ) : searchTerm.trim() ? (
            <><h2>No integrations found</h2><p>Try searching for a different integration.</p><Button onClick={() => setSearchTerm("")} size="sm" type="button" variant="secondary">Clear search</Button></>
          ) : statusFilter === "connected" && cards.length ? (
            <><h2>No connected integrations yet</h2><p>Connect an integration to see it in this list.</p><Button onClick={() => setStatusFilter("all")} size="sm" type="button" variant="secondary">View all integrations</Button></>
          ) : (
            <><h2>No integrations available</h2><p>Connect an integration to start sending your group data to another tool.</p></>
          )}
        </div>
      )}

      {groupPickerProvider && (
        <IntegrationGroupPicker
          groups={groups}
          onChoose={chooseGroup}
          onClose={() => setGroupPickerProvider(null)}
          provider={groupPickerProvider}
        />
      )}

      {dialog && (
        <IntegrationDialog
          connection={dialog.connection}
          group={dialog.group}
          hasFormChanges={hasFormChanges}
          isDeleting={isDeleting}
          isSaving={isSaving}
          submitState={isSaving ? "loading" : "idle"}
          onChange={handleFieldChange}
          onBack={handleBackToGroups}
          onClose={handleCloseDialog}
          onDelete={handleDelete}
          onSubmit={handleSubmit}
          provider={dialog.provider}
          values={formValues}
        />
      )}


      {deleteConfirmation && (
        <IntegrationDeleteDialog
          group={deleteConfirmation.group}
          isDeleting={isDeleting}
          onCancel={cancelDelete}
          onConfirm={confirmDelete}
          provider={deleteConfirmation.provider}
        />
      )}
      </section>
    </main>
  );
}

export default Integrations;
